import type { Location } from 'react-router-dom';
import { OrderStatus } from 'src/types';
import { EditOrderGuardInput, EditOrderGuardResult } from 'src/types/loaders';
import {
  isClinicAdmin,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
} from 'src/utils';

/** Fields needed by list row links and edit guards (detail, list, or base order). */
type LdtOrderEditFields = {
  id: string;
  status: OrderStatus;
  createdByCs?: boolean;
  creatorAuth0Id?: string | null;
};

export function getRowLink(
  order: LdtOrderEditFields,
  organization: string,
  location: Location,
  userId: string,
): {
  pathname: string;
  state: { background: { pathname: string; search: string } };
} {
  if (order.status === OrderStatus.Draft && canEditOrder(order, userId)) {
    return {
      pathname: `/${organization}/orders/${order.id}/edit`,
      state: {
        background: { pathname: location.pathname, search: location.search },
      },
    };
  }

  return {
    pathname: `/${organization}/orders/${order.id}`,
    state: {
      background: { pathname: location.pathname, search: location.search },
    },
  };
}

/**
 * Whether the user can edit an order.
 */
export const canEditOrder = (
  order: LdtOrderEditFields,
  userInfoId?: string,
): boolean => {
  if (order.status === OrderStatus.Draft) {
    const canCSOrSalesUserEditCSOrder =
      isCustomerSuccessOrSalesUser() && order.createdByCs;
    const canPhysicianOrClinicAdminEditNonCSOrder =
      (isClinicAdmin() || isOrderingPhysician()) && !order.createdByCs;

    return (
      canPhysicianOrClinicAdminEditNonCSOrder ||
      canCSOrSalesUserEditCSOrder ||
      order.creatorAuth0Id === userInfoId
    );
  } else if (order.status === OrderStatus.QualityControlPending) {
    return isCustomerSuccessOrSalesUser();
  } else {
    return false;
  }
};

/**
 * Clinic workflow: who can access the order edit page and when.
 * Draft: must pass canEditDraftOrder or be CS/Sales on a CS-created order / creator.
 * QC Pending: only CS/Sales. Other statuses: redirect.
 */
export function getEditOrderRedirect(
  input: EditOrderGuardInput,
): EditOrderGuardResult {
  const { order, userId, redirectBasePath, orderId } = input;
  const redirectPath = `${redirectBasePath}/orders/${orderId}`;

  if (!canEditOrder(order, userId)) {
    if (order.status === OrderStatus.Draft) {
      return {
        redirectPath,
        errorMessage: 'You are not authorized to edit this order.',
      };
    } else {
      return {
        redirectPath,
        errorMessage: 'This order cannot be edited at this time.',
      };
    }
  }

  return null;
}
